import React from "react";
import { Box, SxProps } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { GroupedServices } from "../../hooks/useSchedulingData";

type SchedulingTableProps = {
  groups: GroupedServices[];
  isWeekly: boolean;
  labels: {
    serviceName: string;
    week: string;
    quarter: string;
  };
  // Style overrides for different contexts (PDF vs UI)
  styleOverrides?: {
    sectionTitle?: SxProps;
    tableContainer?: SxProps;
    headerText?: SxProps;
    headerPeriodCell?: SxProps;
    headerPeriodLabel?: SxProps;
    headerPeriodNumber?: SxProps;
    categoryCell?: SxProps;
    categoryText?: SxProps;
    serviceName?: SxProps;
    valueCell?: SxProps;
    valueText?: SxProps;
  };
};

const SchedulingTable: React.FC<SchedulingTableProps> = ({
  groups,
  isWeekly,
  labels,
  styleOverrides = {},
}) => {
  const theme: Theme = useTheme();

  if (groups.length === 0) return null;

  const renderServiceTypeGroup = (group: GroupedServices) => {
    return (
      <Box key={group.serviceTypeCode} sx={styles.serviceTypeGroup(theme)}>
        {/* Category Cell (left side, spans vertically) */}
        <Box
          sx={{ ...styles.categoryCell(theme), ...styleOverrides.categoryCell }}
        >
          <Box
            component="p"
            sx={{
              ...styles.categoryText(theme),
              ...styleOverrides.categoryText,
            }}
          >
            {group.serviceTypeName}
          </Box>
        </Box>

        {/* Service Rows (right side, stacked vertically) */}
        <Box sx={styles.serviceRowsContainer}>
          {group.services.map((service, itemIndex) => (
            <Box key={itemIndex} sx={styles.serviceRow(theme)}>
              <Box sx={styles.serviceNameCell(theme)}>
                <Box
                  component="p"
                  sx={{
                    ...styles.serviceName(theme),
                    ...styleOverrides.serviceName,
                  }}
                >
                  {service.serviceName}
                </Box>
              </Box>
              {service.scheduleValues.map((value, valueIndex) => (
                <Box
                  key={valueIndex}
                  sx={{
                    ...styles.valueCell(theme),
                    ...styleOverrides.valueCell,
                  }}
                >
                  <Box
                    component="p"
                    sx={{
                      ...styles.valueText(theme),
                      ...styleOverrides.valueText,
                    }}
                  >
                    {value ? value : "-"}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={styles.section}>
      {/* Table Container */}
      <Box
        sx={{
          ...styles.tableContainer(theme),
          ...styleOverrides.tableContainer,
        }}
      >
        {/* Table Header Row */}
        <Box sx={styles.headerRow(theme)}>
          {/* "Services" label spanning category + service name columns */}
          <Box sx={styles.headerServicesCell}>
            <Box
              component="p"
              sx={{
                ...styles.headerText(theme),
                ...styleOverrides.headerText,
              }}
            >
              {labels.serviceName}
            </Box>
          </Box>

          {/* Period Headers */}
          {["1", "2", "3", "4"].map((header, index) => (
            <Box
              key={index}
              sx={{
                ...styles.headerPeriodCell,
                ...styleOverrides.headerPeriodCell,
              }}
            >
              <Box sx={styles.headerPeriodContent}>
                <Box
                  component="p"
                  sx={{
                    ...styles.headerPeriodLabel(theme),
                    ...styleOverrides.headerPeriodLabel,
                  }}
                >
                  {isWeekly ? labels.week : labels.quarter}
                </Box>
                <Box
                  component="p"
                  sx={{
                    ...styles.headerPeriodNumber(theme),
                    ...styleOverrides.headerPeriodNumber,
                  }}
                >
                  {header}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Service Type Groups */}
        {groups.map((group) => renderServiceTypeGroup(group))}
      </Box>
    </Box>
  );
};

export default SchedulingTable;

// Base styles - can be overridden via styleOverrides prop
const styles = {
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  tableContainer: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    overflow: "hidden",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
  }),
  headerRow: (theme: Theme) => ({
    display: "flex",
    gap: "16px",
    padding: "6px 12px",
    backgroundColor: theme.colorTokens.P[10], // #E6F0FF
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  headerServicesCell: {
    flex: 1,
    display: "flex",
    alignItems: "center",
  },
  headerPeriodCell: {
    width: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerPeriodContent: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    alignItems: "center",
  },
  headerText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "normal",
    color: theme.colorTokens.N[90], // #3B3F42
    margin: 0,
  }),
  headerPeriodLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    fontSize: "9px",
    lineHeight: "normal",
    color: theme.colorTokens.N[80], // #6D7075
    margin: 0,
  }),
  headerPeriodNumber: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "normal",
    color: theme.colorTokens.N[90], // #3B3F42
    margin: 0,
  }),
  serviceTypeGroup: (theme: Theme) => ({
    padding: "0px 12px",
    display: "flex",
    gap: "16px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    "&:last-child": {
      borderBottom: "none",
    },
  }),
  categoryCell: (theme: Theme) => ({
    width: "140px",
    display: "flex",
    alignItems: "center",
  }),
  categoryText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "9px",
    lineHeight: "normal",
    color: theme.colorTokens.N[90], // #3B3F42
    margin: 0,
  }),
  serviceRowsContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
  },
  serviceRow: (theme: Theme) => ({
    display: "flex",
    gap: "16px",
  }),
  serviceNameCell: (theme: Theme) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
  }),
  serviceName: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "9px",
    lineHeight: "normal",
    color: theme.colorTokens.N[90], // #3B3F42
    margin: 0,
  }),
  valueCell: (theme: Theme) => ({
    width: "40px",
    padding: "12px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  valueText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "9px",
    lineHeight: "normal",
    color: theme.colorTokens.N[80], // #6D7075
    margin: 0,
    textAlign: "center" as "center",
  }),
};
